import type { LayoutSpec } from '@src/types/LayoutConfig';
import type { PredictionMarketCardConfig } from '@talos/kyoko';

/** Count how many favourites are known Event Series / Event symbols. */
export function countFavoritesInKnownSymbols(
  favoriteSymbols: readonly string[] | undefined,
  knownSymbols: ReadonlySet<string>
): number {
  if (!favoriteSymbols?.length || knownSymbols.size === 0) {
    return 0;
  }
  let count = 0;
  for (const symbol of favoriteSymbols) {
    if (knownSymbols.has(symbol)) {
      count += 1;
    }
  }
  return count;
}

export type PredictionMarketCardCounts = {
  forOutcome: number;
  forAllOutcomes: number;
};

function isConfiguredPredictionCard(card: PredictionMarketCardConfig | undefined): card is PredictionMarketCardConfig {
  return !!card?.id && !!card.eventID;
}

/** Collect configured prediction cards from trading layout dockview panels. */
export function collectPredictionMarketCardsFromLayouts(
  layouts: readonly LayoutSpec[] | undefined
): PredictionMarketCardConfig[] {
  if (!layouts?.length) {
    return [];
  }

  const cards: PredictionMarketCardConfig[] = [];
  for (const layout of layouts) {
    const panels = layout.dockViewLayout?.panels ?? {};
    for (const panel of Object.values(panels)) {
      if (panel.contentComponent !== 'predictionMarkets') {
        continue;
      }
      const columns = (panel.params as { columns?: PredictionMarketCardConfig[][] } | undefined)?.columns;
      if (!columns) {
        continue;
      }
      for (const column of columns) {
        for (const card of column) {
          if (isConfiguredPredictionCard(card)) {
            cards.push(card);
          }
        }
      }
    }
  }
  return cards;
}

/**
 * Count prediction cards by outcome scope:
 * - forOutcome: cards with a specific outcome selected
 * - forAllOutcomes: cards showing all outcomes of an event (no outcomeSymbol)
 */
export function countPredictionMarketCards(cards: readonly PredictionMarketCardConfig[]): PredictionMarketCardCounts {
  let forOutcome = 0;
  let forAllOutcomes = 0;
  for (const card of cards) {
    if (card.outcomeSymbol) {
      forOutcome += 1;
    } else {
      forAllOutcomes += 1;
    }
  }
  return { forOutcome, forAllOutcomes };
}

export function countPredictionMarketCardsFromLayouts(
  layouts: readonly LayoutSpec[] | undefined
): PredictionMarketCardCounts {
  return countPredictionMarketCards(collectPredictionMarketCardsFromLayouts(layouts));
}
